/**
 * Tradução do `caminho` de um erro de condição (`$.todos[1].algum[0].op`) para uma posição no
 * editor: a sequência de índices até o nó e, quando houver, a chave com problema.
 *
 * Usado tanto pelo 400 do backend quanto pelo validador local, que produz o mesmo formato.
 */
export type ChaveCondicao = 'fonte' | 'ref' | 'op' | 'valor'

export type PosicaoCondicao = {
  /** Índices de cada nível, do grupo raiz até o nó. Vazio = erro no próprio grupo raiz. */
  indices: number[]
  /** Chave da linha com problema; ausente quando o erro é do nó ou do grupo inteiro. */
  chave?: ChaveCondicao
  /** Índice dentro do valor, para `em` (`$.todos[0].valor[2]`). */
  indiceValor?: number
}

const CHAVES: ChaveCondicao[] = ['fonte', 'ref', 'op', 'valor']

/** `null` quando o caminho não é reconhecível (erro sem destino na tela). */
export const analisarCaminho = (caminho: string | null | undefined): PosicaoCondicao | null => {
  if (!caminho || !caminho.startsWith('$')) return null
  if (caminho === '$') return { indices: [] }

  const indices: number[] = []
  let chave: ChaveCondicao | undefined
  let indiceValor: number | undefined

  const partes = caminho
    .slice(1)
    .split('.')
    .filter((parte) => parte !== '')
  for (const parte of partes) {
    const grupo = /^(todos|algum)(\[(\d+)\])?$/.exec(parte)
    if (grupo) {
      if (grupo[3] !== undefined) indices.push(Number(grupo[3]))
      continue
    }
    const campo = /^(fonte|ref|op|valor)(\[(\d+)\])?$/.exec(parte)
    if (campo && CHAVES.includes(campo[1] as ChaveCondicao)) {
      chave = campo[1] as ChaveCondicao
      if (campo[3] !== undefined) indiceValor = Number(campo[3])
      continue
    }
    return null
  }
  return { indices, chave, indiceValor }
}

/** Chave estável de um nó da árvore de condição, usada para casar erros com a linha na tela. */
export const chaveDoNo = (indices: number[]): string => indices.join('.')

/** Erro de condição já posicionado na árvore do editor. */
export type ErroPosicionado = {
  caminho: string
  codigo: string
  mensagem: string
  posicao: PosicaoCondicao | null
}

export const posicionarErros = (
  erros: Array<{ caminho?: string | null; codigo?: string | null; mensagem?: string | null }>
): ErroPosicionado[] =>
  erros.map((erro) => ({
    caminho: erro.caminho ?? '$',
    codigo: erro.codigo ?? '',
    mensagem: erro.mensagem ?? '',
    posicao: analisarCaminho(erro.caminho),
  }))

/** Mensagens que caem exatamente neste nó da árvore (mesmo conjunto de índices). */
export const errosDoNo = (erros: ErroPosicionado[], indices: number[]): ErroPosicionado[] => {
  const alvo = chaveDoNo(indices)
  return erros.filter((erro) => erro.posicao && chaveDoNo(erro.posicao.indices) === alvo)
}
